/**
 * 路由转换工具
 * 将后端返回的菜单数据转换为前端路由配置
 */
import type { RouteConfig } from '@/types/route'
import type { MenuTreeNode } from '@/api/menu'
import componentMap from './componentMap'
import OutletOnly from '@/components/layout/OutletOnly'

/**
 * 将菜单节点转换为路由配置
 * @param menu 菜单节点
 * @returns 路由配置
 */
export function transformMenuToRoute(menu: MenuTreeNode): RouteConfig | null {
  // 处理 file_path：移除前导斜杠
  const filePath = menu.file_path?.startsWith('/') ? menu.file_path.slice(1) : menu.file_path

  // 如果有 file_path，尝试从组件映射中获取组件
  const component = filePath ? componentMap[filePath] : undefined

  // 如果没有对应的组件，使用 OutletOnly 作为父级容器
  const routeComponent = component || OutletOnly

  // 处理路径：
  // - "/" 保持不变（根路径/首页）
  // - 其他路径：移除前导斜杠，然后提取最后一段作为相对路径（用于嵌套路由）
  //   例如："/basic/road/static" -> "static"
  //        "/basic/road" -> "road"
  //        "/basic" -> "basic"
  let routePath = ''
  if (menu.url === '/') {
    routePath = ''
  } else {
    // 移除前导斜杠并提取最后一段路径
    const cleanPath = menu.url.startsWith('/') ? menu.url.slice(1) : menu.url
    const segments = cleanPath.split('/').filter(Boolean)
    routePath = segments[segments.length - 1] || ''
  }

  // console.log('[transformMenuToRoute] menu:', menu.menu_name, 'url:', menu.url, 'routePath:', routePath, 'filePath:', filePath, 'componentFound:', !!component)

  const route: RouteConfig = {
    path: routePath,
    component: routeComponent,
    title: menu.title || menu.menu_name,
    titleEn: menu.title_en || '',
    authRequired: true,
    hidden: menu.is_show === 0,
    hideBreadcrumbTitle: Boolean((component as { hideBreadcrumbTitle?: boolean } | undefined)?.hideBreadcrumbTitle),
    hideGlobalBreadcrumb: Boolean((component as { hideGlobalBreadcrumb?: boolean } | undefined)?.hideGlobalBreadcrumb),
  }

  // 递归处理子菜单
  if (menu.child && menu.child.length > 0) {
    const children = menu.child
      .map(transformMenuToRoute)
      .filter((child): child is RouteConfig => child !== null)

    if (children.length > 0) {
      route.children = children
    }
  }

  return route
}

/**
 * 将菜单树列表转换为路由配置列表
 * @param menus 菜单树列表
 * @returns 路由配置列表
 */
export function transformMenusToRoutes(menus: MenuTreeNode[]): RouteConfig[] {
  const routes = menus
    .map(transformMenuToRoute)
    .filter((route): route is RouteConfig => route !== null)
  // console.log('[transformMenusToRoutes] Generated routes:', JSON.stringify(routes, null, 2))
  return routes
}
